import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { estadoComOrigem } from "@/rotas";

/*
 * A tabela densa do painel — doc 06a §1 e §5.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ DENSIDADE É O REQUISITO, E NÃO O ENFEITE.                                │
 * │                                                                          │
 * │ Linha de 40px, corpo de 13px. "É uma ferramenta de trabalho: preciso de  │
 * │ mais linhas por tela." Um operador varrendo mil boletos atrás do que     │
 * │ quebrou não pode rolar seis vezes o que caberia em uma.                  │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Escrita à mão, sem TanStack Table. A tabela desta tela não ordena no cliente
 * (a ordem vem do banco, do mais recente para o mais antigo), não agrupa e não
 * reordena coluna — o que sobraria da biblioteca seria a marcação, que é o que
 * está aqui. Quando a tela de fila morta pedir agrupamento de verdade, a
 * biblioteca entra com motivo.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ COM `hrefDe`, A LINHA É UM LINK DE VERDADE.                              │
 * │                                                                          │
 * │ Ctrl+clique abre em aba nova, o botão do meio também, e o menu do        │
 * │ navegador oferece copiar o endereço. Um `<a>` não pode envolver a linha, │
 * │ porque o menu ••• e o interruptor moram nela, e controle dentro de link  │
 * │ é HTML inválido. Então o `<a>` é uma camada `absolute inset-0` sobre a   │
 * │ linha, e a coluna `interativa` sobe acima dela.                          │
 * │                                                                          │
 * │ Em teste, clique no link pelo papel (`getByRole("link")`): o jsdom não   │
 * │ tem layout, e o clique no texto da célula não acerta a camada.           │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ QUEM ROLA É `main`, E MAIS NINGUÉM.                                      │
 * │                                                                          │
 * │ `overflow-x-auto` aqui criava um segundo rolador: o cabeçalho `sticky`   │
 * │ grudava nele, que não rola na vertical, e a barra horizontal ficava      │
 * │ depois da última linha. `overflow-clip` só recorta os cantos, e não é    │
 * │ rolador.                                                                 │
 * │                                                                          │
 * │ A tabela cabe em vez de rolar: mede a largura que tem e tira as colunas  │
 * │ pela ordem de `descarte` (doc 06a §7, até 1024px). Menos que isso,       │
 * │ `min-w-min` empurra `main`, e a barra dele fica sempre à vista.          │
 * │                                                                          │
 * │ jsdom não mede layout: sem medida, todas as colunas ficam. Mudou largura │
 * │ ou coluna, confira num navegador de verdade, em 1440 e em 1024.          │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

export interface Coluna<T> {
  chave: string;
  rotulo: string;
  /** Largura em `grid-template-columns`. Fixa: coluna que dança cansa a leitura. */
  largura: string;
  /**
   * Ordem em que a coluna sai quando a tabela não cabe: `1` sai primeiro, e colunas com o
   * mesmo número saem juntas. Sem isto ela nunca sai: é o caso de `Ações` e de quem
   * identifica a linha.
   */
  descarte?: number;
  /** Dinheiro e datas alinham à direita. */
  direita?: boolean;
  /** Célula com controle próprio, como `MenuDeLinha`: usá-la não abre a linha. */
  interativa?: boolean;
  render: (item: T) => ReactNode;
}

/** A linha ou abre um painel local, ou é link para um caminho. */
type ComoAbrir<T> =
  | { aoAbrir: (item: T) => void; hrefDe?: undefined; rotuloDe?: undefined }
  | {
      hrefDe: (item: T) => string;
      /** O nome acessível do link: a primeira célula pode ser só um selo. */
      rotuloDe: (item: T) => string;
      aoAbrir?: undefined;
    };

/** `px-3` dos dois lados da linha, mais o fio do cartão. */
const RESPIRO_DA_LINHA = 26;
/** O `gap-3` da grade. */
const VAO_ENTRE_COLUNAS = 12;

/** O piso da coluna: o primeiro valor em pixels de `168px` e de `minmax(200px, 2fr)`. */
function pisoDaColuna(largura: string): number {
  return Number(/(\d+)px/.exec(largura)?.[1] ?? 0);
}

function pisoDaTabela(colunas: readonly { largura: string }[]): number {
  const soma = colunas.reduce((total, coluna) => total + pisoDaColuna(coluna.largura), 0);
  return soma + VAO_ENTRE_COLUNAS * (colunas.length - 1) + RESPIRO_DA_LINHA;
}

/*
 * O limiar sai dos próprios pisos, e não de um número escrito em cada coluna: um limiar à
 * mão envelhece na primeira coluna que mudar de largura, e aí a tabela volta a cortar.
 */
function colunasQueCabem<T>(colunas: readonly Coluna<T>[], disponivel: number) {
  const ordens = [...new Set(colunas.flatMap((coluna) => coluna.descarte ?? []))].sort(
    (a, b) => a - b,
  );

  let visiveis = colunas;
  for (const ordem of ordens) {
    if (pisoDaTabela(visiveis) <= disponivel) break;
    visiveis = visiveis.filter((coluna) => coluna.descarte !== ordem);
  }
  return visiveis;
}

/**
 * A largura de um elemento, em `ref` de função porque a medida precisa sair antes da
 * pintura: em efeito passivo, a tela de 1024 piscaria com a tabela cortada. Zero é "sem
 * medida", que é o jsdom.
 */
function useLarguraMedida() {
  const [largura, setLargura] = useState(0);

  const medir = useCallback((elemento: HTMLElement | null) => {
    if (elemento === null || typeof ResizeObserver === "undefined") return undefined;

    setLargura(elemento.clientWidth);
    const observador = new ResizeObserver(() => setLargura(elemento.clientWidth));
    observador.observe(elemento);
    return () => observador.disconnect();
  }, []);

  return [medir, largura] as const;
}

function paraNaCelula(evento: MouseEvent | KeyboardEvent) {
  evento.stopPropagation();
}

function LinkDaLinha({ para, rotulo }: { para: string; rotulo: string }) {
  const local = useLocation();

  return (
    <Link
      to={para}
      // O "« voltar" da página volta para esta lista, com os filtros dela.
      state={estadoComOrigem(local)}
      data-linha=""
      aria-label={rotulo}
      // `j` e `k` focam esta camada: sem a margem, a linha para embaixo do cabeçalho fixo.
      className="absolute inset-0 scroll-mt-10 -outline-offset-2"
    />
  );
}

export function Tabela<T>({
  colunas: todas,
  itens,
  chaveDe,
  aoAbrir,
  hrefDe,
  rotuloDe,
  rotulo,
  selecionado,
  vazio,
  carregando = false,
}: {
  colunas: readonly Coluna<T>[];
  itens: readonly T[];
  chaveDe: (item: T) => string;
  /** Nome acessível da tabela. */
  rotulo?: string;
  /** Chave do item aberto no painel lateral, marcado com o traço de acento. */
  selecionado?: string | undefined;
  /** Uma linha curta, e nunca "Nenhum dado encontrado" (doc 06a §6). */
  vazio: ReactNode;
  carregando?: boolean;
} & ComoAbrir<T>) {
  const corpo = useRef<HTMLDivElement>(null);
  const [medir, largura] = useLarguraMedida();

  const visiveis = largura === 0 ? todas : colunasQueCabem(todas, largura);
  const grade = visiveis.map((coluna) => coluna.largura).join(" ");

  // A camada do link mora na primeira célula sem controle próprio.
  const celulaDoLink = visiveis.findIndex((coluna) => coluna.interativa !== true);

  /*
   * `j` e `k` andam nas linhas, `Enter` abre — doc 06a §4.2.
   *
   * O atalho é ignorado quando o foco está num campo de texto: um operador
   * digitando "jaqueline" na busca não pode ver a lista pular a cada `j`. Com menu
   * ou diálogo aberto também: a lista está atrás, e a tecla é de quem está na
   * frente.
   */
  useEffect(() => {
    function aoTeclar(evento: globalThis.KeyboardEvent) {
      if (evento.key !== "j" && evento.key !== "k") return;

      const alvo = evento.target as HTMLElement | null;
      const digitando =
        alvo instanceof HTMLInputElement ||
        alvo instanceof HTMLTextAreaElement ||
        alvo instanceof HTMLSelectElement ||
        alvo?.isContentEditable === true;
      if (digitando || evento.metaKey || evento.ctrlKey || evento.altKey) return;
      if (alvo?.closest?.('[role="menu"], [role="dialog"]')) return;

      const linhas = Array.from(
        corpo.current?.querySelectorAll<HTMLElement>("[data-linha]") ?? [],
      );
      if (linhas.length === 0) return;

      const atual = linhas.findIndex((linha) => linha === document.activeElement);
      const proximo =
        evento.key === "j"
          ? Math.min(atual + 1, linhas.length - 1)
          : Math.max(atual - 1, 0);

      evento.preventDefault();
      linhas[atual === -1 ? 0 : proximo]?.focus();
    }

    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, []);

  return (
    // A medida fica fora do cartão: o `min-w-min` dele não pode inflar a largura medida.
    <div ref={medir}>
      <div className="min-w-min overflow-clip rounded-cartao border border-fio bg-superficie">
        <div
          role="table"
          aria-busy={carregando}
          {...(rotulo !== undefined ? { "aria-label": rotulo } : {})}
        >
          {/*
            Cabeçalho fixo: numa lista de 200 linhas, saber qual coluna é qual
            depois da terceira rolagem não pode depender de memória. `-top-3` desconta o
            `p-3` de `main`: o `sticky` para na borda do conteúdo, e as linhas apareceriam
            na faixa de 12px acima do cabeçalho.
          */}
          <div
            role="row"
            className="sticky -top-3 z-10 grid items-center gap-3 border-b border-fio bg-superficie px-3 py-2"
            style={{ gridTemplateColumns: grade }}
          >
            {visiveis.map((coluna) => (
              <div
                key={coluna.chave}
                role="columnheader"
                className={`rotulo truncate ${coluna.direita === true ? "text-right" : ""}`}
              >
                {coluna.rotulo}
              </div>
            ))}
          </div>

          <div ref={corpo} role="rowgroup">
            {carregando ? (
              /*
                Esqueleto com a ALTURA EXATA da linha real (doc 06a §7). Sem isso,
                a tabela salta quando os dados chegam e o operador perde o lugar.
              */
              Array.from({ length: 8 }, (_, indice) => (
                <div
                  key={indice}
                  aria-hidden="true"
                  className="h-[var(--altura-linha-tabela)] border-b border-fio/50"
                />
              ))
            ) : itens.length === 0 ? (
              <div role="row">
                <div role="cell" className="px-4 py-8 text-texto-2">
                  {vazio}
                </div>
              </div>
            ) : (
              itens.map((item) => {
                const chave = chaveDe(item);
                const ativa = chave === selecionado;
                const abreNaLinha = aoAbrir !== undefined;

                return (
                  <div
                    key={chave}
                    role="row"
                    {...(aoAbrir !== undefined
                      ? {
                          "data-linha": "",
                          tabIndex: 0,
                          onClick: () => aoAbrir(item),
                          onKeyDown: (evento: KeyboardEvent) => {
                            if (evento.key === "Enter" || evento.key === " ") {
                              evento.preventDefault();
                              aoAbrir(item);
                            }
                          },
                        }
                      : {})}
                    className={[
                      "relative grid h-[var(--altura-linha-tabela)] scroll-mt-10 cursor-pointer items-center gap-3",
                      "border-b border-fio/50 px-3 text-corpo",
                      // Linha ativa marcada por traço à esquerda no acento — e não
                      // por fundo colorido, que competiria com a paleta de estados.
                      ativa
                        ? "bg-superficie-2 shadow-[inset_2px_0_0_var(--esferografica)]"
                        : "hover:bg-superficie-2",
                    ].join(" ")}
                    style={{ gridTemplateColumns: grade }}
                  >
                    {visiveis.map((coluna, indice) => (
                      <div
                        key={coluna.chave}
                        role="cell"
                        {...(coluna.interativa === true && abreNaLinha
                          ? { onClick: paraNaCelula, onKeyDown: paraNaCelula }
                          : {})}
                        className={[
                          "truncate",
                          coluna.direita === true ? "text-right" : "",
                          coluna.interativa === true ? "relative z-[1] cursor-default" : "",
                        ].join(" ")}
                      >
                        {hrefDe !== undefined && indice === celulaDoLink && (
                          <LinkDaLinha para={hrefDe(item)} rotulo={rotuloDe(item)} />
                        )}
                        {coluna.render(item)}
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
